import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, AlertCircle } from "lucide-react";
import Layout from "../components/Layout";
import StepIndicator from "../components/StepIndicator";
import BackButton from "../components/BackButton";
import PrimaryButton from "../components/PrimaryButton";
import { formatCPF, onlyDigits } from "../lib/format";
import { buscarPacientePorCpf } from "../lib/api";
import { setBooking, clearBooking } from "../lib/bookingState";

const STEPS = ["CPF", "Tipo", "Especialidade", "Profissional", "Horario", "Confirmar"];

export default function AgendarCPF() {
  const navigate = useNavigate();
  const [cpf, setCpf] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const digits = onlyDigits(cpf);
  const valid = digits.length === 11;

  async function handleSubmit(e) {
    e.preventDefault();

    if (!valid || loading) return;

    setLoading(true);
    setError("");

    try {
      clearBooking();

      const paciente = await buscarPacientePorCpf(digits);

      setBooking({
        cpf: digits,
        patient_id: paciente.patient_id,
        paciente_nome: paciente.nome,
        telefone: paciente.telefone || "",
        email: paciente.email || "",
        convenio_id: paciente.convenio_id || null,
        convenio_plano_id: paciente.convenio_plano_id || null,
        convenio_nome: paciente.convenio_nome || "",
        matricula: paciente.matricula || "",
        titular: paciente.titular || "",
        tem_convenio: Boolean(paciente.convenio_id),
        exige_guia: Boolean(paciente.exige_guia),
        mensagem_guia: paciente.mensagem_guia || "",
      });

      toast.success(`Olá, ${paciente.nome.split(" ")[0]}!`);
      navigate("/agendar/tipo");
    } catch (err) {
      const detail = err?.response?.data?.detail;

      if (detail === "cpf_invalido") {
        setError("Por favor, verifique o CPF informado.");
      } else if (detail === "paciente_nao_encontrado") {
	  setBooking({ cpf: digits });
	  navigate("/agendar/pre-cadastro");
	  } else {
        setError("Não foi possível buscar agora. Tente novamente em instantes.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <BackButton to="/" />
      <StepIndicator steps={STEPS} current={1} />

      <div className="fade-up">
        <h1
          className="text-2xl sm:text-3xl tracking-tight font-bold text-slate-900"
          style={{ fontFamily: "Manrope" }}
        >
          Vamos começar
        </h1>
        <p className="text-slate-600 mt-2">
          Informe seu CPF para localizarmos seu cadastro na clínica.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">CPF</span>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            data-testid="input-cpf"
            value={cpf}
            onChange={(e) => setCpf(formatCPF(e.target.value))}
            placeholder="000.000.000-00"
            className="mt-2 w-full h-14 px-4 rounded-xl border border-slate-200 bg-white text-slate-900 text-lg tracking-wide outline-none focus:ring-2 focus:ring-[#00357C]/30 focus:border-[#00357C] transition"
            maxLength={14}
          />
        </label>

        {error && (
          <div
            data-testid="cpf-error"
            className="flex items-start gap-2 bg-red-50 text-red-700 border border-red-100 rounded-xl px-4 py-3 text-sm"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        <PrimaryButton type="submit" disabled={!valid || loading} data-testid="cpf-submit">
          {loading ? "Buscando..." : "Continuar"} {!loading && <ArrowRight size={18} />}
        </PrimaryButton>

        <p className="text-xs text-center text-slate-400 pt-2">
          Seus dados são usados apenas para localizar seu cadastro.
        </p>
      </form>
    </Layout>
  );
}